import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { lineReveal } from "@/lib/motion";
import CTAButton from "@/components/CTAButton";import { ShieldCheck, Clock3, Stethoscope, Award } from "lucide-react";

const HEADLINE = ["Most Liver Diseases", "Develop Silently.", "Know Your Risk in", "Just 2 Minutes."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden bg-sa-bg pt-32 pb-12 md:pt-40 md:pb-16"
      data-testid="hero-section"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 md:px-12 lg:grid-cols-12">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.7 }}
            className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-sa-copper/40 bg-sa-copper/10 px-5 py-2.5 text-sm font-semibold tracking-tight text-sa-forest shadow-[0_10px_30px_-12px_rgba(198,123,92,0.5)]"
            data-testid="hero-badge"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sa-copper opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-sa-copper" />
            </span>
            India's first physician-led precision metabolic liver health platform
          </motion.div>

          <h1 className="font-display text-[2.3rem] font-medium leading-[1.05] tracking-tight text-sa-forest sm:text-5xl lg:text-[3.6rem]">
            {HEADLINE.map((line, i) => (
              <span key={i} className="reveal-mask">
                <motion.span
                  custom={i}
                  variants={lineReveal}
                  initial="hidden"
                  animate="show"
                  className="block"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="mt-7 max-w-xl text-lg leading-relaxed text-sa-muted"
          >
            Take India's smartest physician-designed liver health assessment to know your
            personalized Liver Risk Score, estimated liver age, and expert recommendations
            before symptoms appear.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="mt-9 grid grid-cols-1 gap-3 sm:grid-cols-3"
          >
            <CTAButton testId="hero-start-assessment" size="sm" className="w-full justify-center">
              Start Free Assessment
            </CTAButton>

            {/* Book Evaluation — same copper pill as the assessment CTA;
                goes to the dedicated /book page (QR-friendly). */}
            <CTAButton testId="hero-book-appointment" to="/book" size="sm" className="w-full justify-center">
              Book Evaluation
            </CTAButton>

            {/* Online Consultation — video consult with a liver specialist,
                dedicated /consultation page. */}
            <CTAButton testId="hero-online-consultation" to="/consultation" size="sm" className="w-full justify-center">
              Online Consultation
            </CTAButton>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-sa-muted"
          >
            <span className="flex items-center gap-1.5">
              <Stethoscope className="h-4 w-4 text-sa-copper" /> Physician-Led
            </span>
            <span className="flex items-center gap-1.5">
              <Award className="h-4 w-4 text-sa-copper" /> Evidence-Based
            </span>
            <span className="flex items-center gap-1.5">
              <Clock3 className="h-4 w-4 text-sa-copper" /> 2 minutes
            </span>
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="h-4 w-4 text-sa-copper" /> Private
            </span>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative h-[420px] overflow-hidden rounded-[32px] border border-sa-beige bg-gradient-to-br from-[#f4f0e7] via-[#eef1ec] to-[#e6ede8] md:h-[520px]"
          >
            <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-sa-copper/15 blur-3xl" />
            <div className="pointer-events-none absolute -left-12 bottom-24 h-56 w-56 rounded-full bg-sa-sage/25 blur-3xl" />

            <motion.div
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 flex items-center justify-center px-4 pb-20 pt-6"
            >
              <img
                src="/liver.png?v=3"
                alt="Human liver anatomy"
                className="h-full w-full scale-105 object-contain drop-shadow-[0_22px_36px_rgba(10,46,34,0.14)]"
              />
            </motion.div>

            <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/70 bg-white/70 p-5 backdrop-blur-md">
              <div className="flex items-center justify-between">
                <p className="text-[13px] font-medium uppercase tracking-widest text-sa-copper">
                  Liver Risk Profile
                </p>
                <span className="flex items-center gap-1.5 text-[11px] font-medium text-sa-forest">
                  <span className="h-2 w-2 rounded-full bg-risk-green" /> Low-risk zone
                </span>
              </div>
              <div className="mt-3 flex gap-1.5">
                <span className="h-2.5 flex-1 rounded-full bg-risk-green" />
                <span className="h-2.5 flex-1 rounded-full bg-risk-yellow" />
                <span className="h-2.5 flex-1 rounded-full bg-risk-orange" />
                <span className="h-2.5 flex-1 rounded-full bg-risk-red" />
              </div>
              <div className="mt-2 flex justify-between text-[11px] text-sa-muted">
                <span>Green</span>
                <span>Yellow</span>
                <span>Orange</span>
                <span>Red</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
